'use client'

import React, { FunctionComponent } from 'react'
import SkillBar from '@elements/SkillBar'
import { motion } from 'framer-motion'

type Props = {
  skillSets: SkillSet[]
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
}

const itemVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.3 } },
}

const SkillSection: FunctionComponent<Props> = ({ skillSets }) => {
  return (
    <section className="py-20 flex justify-center w-full">
      <div className="max-w-[72rem] w-full px-8">
        <div className="mb-12">
          <motion.h3 
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl font-bold mb-4 font-lexend text-[var(--foreground)] border-l-4 border-[#f90] pl-6"
          >
            Skills
          </motion.h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-min">
          {skillSets.map((skillSet: SkillSet, index) => {
             // Optional: Make "Infrastructure" span 2 cols on medium/large screens if it has many items
             const isLargeGroup = skillSet.skills.length > 6
             const colSpanClass = isLargeGroup ? 'md:col-span-2 lg:col-span-2' : ''
             
             return (
              <motion.div
                key={skillSet.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ delay: index * 0.1 }}
                className={`bg-white/50 dark:bg-black/20 backdrop-blur-sm rounded-2xl p-6 border border-black/5 dark:border-white/5 shadow-sm hover:shadow-md transition-shadow ${colSpanClass}`}
              >
                <div className="mb-4 flex items-center gap-3">
                  <h4 className="text-xl font-bold text-[var(--foreground)]">
                    {skillSet.name}
                  </h4>
                </div>
                
                <motion.div 
                  variants={containerVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  className="flex flex-wrap gap-3"
                >
                  {skillSet.skills.map(({ code, name, logoPath }: Skill) => (
                    <motion.div key={code} variants={itemVariants}>
                      <SkillBar name={name} logo={logoPath} />
                    </motion.div>
                  ))}
                </motion.div>
              </motion.div>
             )
          })}
        </div>
      </div>
    </section>
  )
}

export default SkillSection
